/**
 * This file contains the source of truth for themes and all associated meta data.
 */
import type { ThemeColorModes } from './theme-color-modes';
import { themeIds, type ThemeIds } from './theme-ids';
import type { ThemeState } from './theme-state';

/**
 * Themes: The internal identifier of a theme.
 * These ids are what the actual theme files/folders are called.
 * style-dictionary will attempt to locate these in the file-system.
 */
export type Themes =
	| 'UNSAFE-dynamic'
	| 'UNSAFE-dynamic-dark'
	| 'atlassian-light'
	| 'atlassian-light-future'
	| 'atlassian-light-increased-contrast'
	| 'UNSAFE-test-light'
	| 'atlassian-dark'
	| 'atlassian-dark-future'
	| 'atlassian-dark-increased-contrast'
	| 'UNSAFE-test-dark'
	| 'atlassian-shape'
	| 'atlassian-spacing'
	| 'atlassian-typography'
	| 'UNSAFE-typography'
	| 'atlassian-motion';

/**
 * ThemeOverrides: The internal identifier of a theme override. Which are themes that contain
 * a subset of tokens intended to override an existing theme. These ids are what the actual
 * theme files/folders are called. style-dictionary will attempt to locate these in the file-system.
 * Theme overrides are temporary and there may not be any defined at times.
 */
type FinesseThemeOverrides =
	| 'atlassian-light-finesse'
	| 'atlassian-light-increased-contrast-finesse'
	| 'atlassian-dark-finesse'
	| 'atlassian-dark-increased-contrast-finesse'
	| 'atlassian-typography-finesse';

// Retain Themes in this public type for backwards compatibility.
// platform-dst-tokens-finesse cleanup: Revisit when the temporary override themes are removed.
export type ThemeOverrides = Themes | FinesseThemeOverrides;

export type ThemeFileNames = Themes | FinesseThemeOverrides;

/**
 * Theme kinds: The type of theme.
 * Some themes are entirely focused on Color, whilst others are purely focused on spacing.
 * In the future other types may be introduced such as typography.
 */
type ThemeKinds = 'color' | 'spacing' | 'typography' | 'shape' | 'motion';

export type DataColorModes = Exclude<ThemeColorModes, 'auto'>;

/**
 * Contrast preferences: The system contrast preference
 */
const themeContrastModes = ['more', 'no-preference', 'auto'] as const;
export type ThemeContrastModes = (typeof themeContrastModes)[number];
export type DataContrastModes = 'more' | 'no-preference' | 'auto';

/**
 * Theme override ids: the equivalent of themeIds for theme overrides.
 * Theme overrides are temporary and there may not be any defined at times.
 */
export const themeOverrideIds = [
	'light-finesse',
	'light-increased-contrast-finesse',
	'dark-finesse',
	'dark-increased-contrast-finesse',
	'typography-finesse',
] as const;

export type ThemeOverrideIds = (typeof themeOverrideIds)[number];

export const themeIdsWithOverrides: readonly [
	'light-increased-contrast',
	'light',
	'light-future',
	'UNSAFE-dynamic',
	'UNSAFE-dynamic-dark',
	'UNSAFE-test-light',
	'dark',
	'UNSAFE-test-dark',
	'dark-future',
	'dark-increased-contrast',
	'spacing',
	'shape',
	'typography',
	'UNSAFE-typography',
	'motion',
	'light-finesse',
	'light-increased-contrast-finesse',
	'dark-finesse',
	'dark-increased-contrast-finesse',
	'typography-finesse',
] = [...themeIds, ...themeOverrideIds];

export type ThemeIdsWithOverrides = (typeof themeIdsWithOverrides)[number];

/**
 * Theme to use a base. This will create the theme as
 * an extension with all token values marked as optional
 * to allow tokens to be overridden as required.
 */
type ExtensionThemeId = ThemeIds;

/**
 * Palettes: The set of base tokens a given theme may be populated with.
 * For example: legacy light & dark themes use the "legacyPalette" containing colors from our
 * previous color set.
 */
export type Palettes =
	| 'defaultPalette'
	| 'spacingScale'
	| 'shapePalette'
	| 'typographyPalette'
	| 'motionPalette';

/**
 * ThemeConfig: the source of truth for all theme meta-data.
 * This object should be used whenever interfacing with themes.
 */
interface ThemeConfig {
	id: ThemeIds | ThemeOverrideIds;
	displayName: string;
	palette: Palettes;
	attributes: (
		| {
				type: 'color';
				// https://developer.mozilla.org/en-US/docs/Web/CSS/color-scheme
				mode: DataColorModes;
		  }
		| {
				type: Extract<ThemeKinds, 'spacing' | 'typography' | 'shape' | 'motion'>;
		  }
	) & {
		/* eslint-disable @repo/internal/deprecations/deprecation-ticket-required */
		/**
		 * @deprecated Use top-level `extends` property instead
		 */
		extends?: ExtensionThemeId;
		/* eslint-enable @repo/internal/deprecations/deprecation-ticket-required */
	};
	/**
	 * Theme to use a base. This will create the theme as
	 * an extension with all token values marked as optional
	 * to allow tokens to be overridden as required.
	 *
	 * The resulting theme css will contain a complete theme.
	 * All tokens specified in the child theme will be overridden.
	 * All tokens not specified in the child theme will be inherited from the parent theme.
	 */
	extends?: ThemeIds;
	/**
	 * Theme to override. This will cause the theme to only
	 * output css variables which can be imported to temporarily
	 * override existing themes for testing purposes.
	 *
	 *
	 * The resulting theme css will be only the tokens specified
	 * and will need the dependant theme to be loaded alongside
	 * it in order to function correctly.
	 */
	override?: ThemeIds;
	/**
	 * Use when the theme provides an increased contrast
	 * version of another theme. This will cause the theme to be loaded
	 * under the media query `@media (prefers-contrast: more)`.
	 */
	increasesContrastFor?: ThemeIds;
}

export const themeConfig: Record<ThemeFileNames, ThemeConfig> = {
	'atlassian-light': {
		id: 'light',
		displayName: 'Light Theme',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'light',
		},
	},
	'atlassian-light-future': {
		id: 'light-future',
		displayName: 'Future Light Theme',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'light',
		},
		override: 'light',
	},
	'atlassian-light-finesse': {
		id: 'light-finesse',
		displayName: 'FY27 Finesse Light Theme Override',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'light',
		},
		override: 'light',
	},
	'atlassian-light-increased-contrast-finesse': {
		id: 'light-increased-contrast-finesse',
		displayName: 'FY27 Finesse Light Theme (increased contrast) Override',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'light',
		},
		override: 'light-increased-contrast',
	},
	'atlassian-light-increased-contrast': {
		id: 'light-increased-contrast',
		displayName: 'Light Theme (increased contrast)',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'light',
		},
		extends: 'light',
		increasesContrastFor: 'light',
	},
	'UNSAFE-dynamic': {
		id: 'UNSAFE-dynamic',
		displayName: 'UNSAFE Dynamic Theme',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'light',
		},
		extends: 'light',
	},
	'UNSAFE-dynamic-dark': {
		id: 'UNSAFE-dynamic-dark',
		displayName: 'UNSAFE Dynamic Dark Theme',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'dark',
		},
		extends: 'dark',
	},
	'UNSAFE-test-light': {
		id: 'UNSAFE-test-light',
		displayName: 'UNSAFE Test Light Theme',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'light',
		},
		extends: 'light',
	},
	'UNSAFE-test-dark': {
		id: 'UNSAFE-test-dark',
		displayName: 'UNSAFE Test Dark Theme',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'dark',
		},
		extends: 'dark',
	},
	'atlassian-dark': {
		id: 'dark',
		displayName: 'Dark Theme',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'dark',
		},
	},

	'atlassian-dark-future': {
		id: 'dark-future',
		displayName: 'Future Dark Theme',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'dark',
		},
		override: 'light',
	},
	'atlassian-dark-finesse': {
		id: 'dark-finesse',
		displayName: 'FY27 Finesse Dark Theme Override',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'dark',
		},
		override: 'dark',
	},
	'atlassian-dark-increased-contrast-finesse': {
		id: 'dark-increased-contrast-finesse',
		displayName: 'FY27 Finesse Dark Theme (increased contrast) Override',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'dark',
		},
		override: 'dark-increased-contrast',
	},
	'atlassian-dark-increased-contrast': {
		id: 'dark-increased-contrast',
		displayName: 'Dark Theme (increased contrast)',
		palette: 'defaultPalette',
		attributes: {
			type: 'color',
			mode: 'dark',
		},
		extends: 'dark',
		increasesContrastFor: 'dark',
	},
	'atlassian-spacing': {
		id: 'spacing',
		displayName: 'Atlassian Spacing',
		palette: 'spacingScale',
		attributes: {
			type: 'spacing',
		},
	},
	'atlassian-typography': {
		id: 'typography',
		displayName: 'Atlassian Typography',
		palette: 'typographyPalette',
		attributes: {
			type: 'typography',
		},
	},
	'UNSAFE-typography': {
		id: 'UNSAFE-typography',
		displayName: 'UNSAFE Dynamic Typography',
		palette: 'typographyPalette',
		attributes: {
			type: 'typography',
		},
		extends: 'typography',
	},
	'atlassian-typography-finesse': {
		id: 'typography-finesse',
		displayName: 'FY27 Finesse Typography Override',
		palette: 'typographyPalette',
		attributes: {
			type: 'typography',
		},
		override: 'typography',
	},
	'atlassian-shape': {
		id: 'shape',
		displayName: 'Shape',
		palette: 'shapePalette',
		attributes: {
			type: 'shape',
		},
	},
	'atlassian-motion': {
		id: 'motion',
		displayName: 'Motion',
		palette: 'motionPalette',
		attributes: {
			type: 'motion',
		},
	},
};

/**
 * Represents theme state once mounted to the page
 * (the page doesn't have an "auto" color mode, it's either light or dark)
 */
export interface ActiveThemeState extends ThemeState {
	colorMode: DataColorModes;
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export default themeConfig;

/**
 * @deprecated Use `import { themeColorModes, ThemeColorModes } from '@atlaskit/tokens/theme-color-modes'` instead.
 */
export { themeColorModes, type ThemeColorModes } from './theme-color-modes';
/**
 * @deprecated Use `import { themeIds, ThemeIds } from '@atlaskit/tokens/theme-ids'` instead.
 */
export { themeIds, type ThemeIds } from './theme-ids';
/**
 * @deprecated Use `import { themeStateDefaults } from '@atlaskit/tokens/theme-state-defaults'` instead.
 */
export { themeStateDefaults } from './theme-state-defaults';
export { type ThemeOptionsSchema, type CSSColor } from './theme-options-schema';
export { type ThemeState } from './theme-state';
